import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { productManagementHomeRoute } from 'config/Api.Config';
import { isMobile } from 'react-device-detect';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import Theme from 'assets/themes/Theme.Common';
import {
  ProductDealerDocumentsAction,
  useProductDealerDocumentProvider,
} from './ProductDocument.DealerDocuments.Hooks';
import { ProductDocumentTabs, useProductDocumentProvider } from '../ProductDocument.Hooks';
import ProductDocumentsDealerFilters from './sections/ProductDocument.DealerDocuments.Filters';
import DealerDocumentList from './sections/ProductDocument.DealerDocuments.List';
import ProductDealerDocumentAdd from './sections/ProductDocument.DealerDocuments.Add';
import ThemeButtonTab from 'assets/components/button/themes/Theme.Button.Tab';

enum Actions {
  ADD = 'add',
  DELETE = 'delete',
}
export type Verify = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof Actions>
>;
export const productDocumentIndexRoute = productManagementHomeRoute + '/productDocument/:id?';
export default function ProductDealerDocumentIndex() {
  const { lang } = getLocales();
  const productDocumentHook = useProductDocumentProvider();
  const {
    views,
    setView,
    currentView,
    dealerDocumentList,
    selectedItems,
    handleDeleteDealerDocumentSubmit,
    toggleSelectAll,
  } = useProductDealerDocumentProvider();

  return (
    <>
      <Page
        class={ThemePageList}
        isLoading={productDocumentHook.isLoadingProductDocument}
        style={{
          pageHeader: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingBottom: 0,
            background: Theme.Color.primary,
            color: Theme.Color.primaryAlt,
            borderBottom: 'none',
          },
        }}
        header={
          <>
            <Card
              class={ThemeCardRow}
              style={{
                cardMedia: {
                  color: Theme.Color.primary,
                  background: Theme.Color.primaryAlt,
                },
              }}
              media={<Icon class="fas-file-contract" />}
              info={lang.productDocuments}
              title={productDocumentHook.productDocument?.displayInfo?.title}
              subtitle={productDocumentHook.productDocument?.productDocumentType}
            />
            <div
              style={{
                height: 'fit-content',
                fontSize: Theme.FontSize.M,
                display: 'flex',
                marginLeft: Theme.Size.pixel(10),
              }}
              children={
                <>
                  <Button
                    key={'dealers-tab'}
                    render={lang.dealers}
                    onClick={productDocumentHook.setSelectedTab}
                    data={ProductDocumentTabs.DEALERS}
                    active={productDocumentHook.selectedTab === ProductDocumentTabs.DEALERS}
                    class={ThemeButtonTab}
                  />
                  <Button
                    key={'dealer-groups-tab'}
                    render={lang.dealerGroup}
                    onClick={productDocumentHook.setSelectedTab}
                    data={ProductDocumentTabs.DEALER_GROUPS}
                    active={productDocumentHook.selectedTab === ProductDocumentTabs.DEALER_GROUPS}
                    class={ThemeButtonTab}
                  />
                </>
              }
            />
          </>
        }
        subHeader={
          <>
            <b style={{ marginRight: 'auto' }} children={lang.productDocuments} />
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-plus" />}
              render={!isMobile && lang.add}
              onClick={setView}
              data={ProductDealerDocumentsAction.ADD}
            />
            &nbsp;
            {selectedItems?.length ? (
              <>
                <Button
                  class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
                  active
                  media={<Icon class="fas-trash" />}
                  render={!isMobile && lang.delete}
                  onClick={setView}
                  data={ProductDealerDocumentsAction.DELETE}
                />
                {currentView === ProductDealerDocumentsAction.DELETE && (
                  <Confirm
                    onConfirm={() => {
                      setView(undefined);
                      handleDeleteDealerDocumentSubmit();
                    }}
                    onClose={() => setView(undefined)}
                    actionName={replaceStringTemplateWithValues(lang.productDealerDocumentDeleteSubmitMessage, {
                      numberOfDocuments: (selectedItems.length ?? 0).toString(),
                    })}
                    confirmationType="nextButton"
                    subcomponents={ThemeConfirmDefault}
                  />
                )}
              </>
            ) : null}
            &nbsp;
            {dealerDocumentList.count > 0 && (
              <>
                <Button
                  class={{ button: ThemeButtonCircle }}
                  media={
                    selectedItems?.length === dealerDocumentList.count
                      ? 'fas-check-square'
                      : selectedItems?.length !== dealerDocumentList.count
                      ? 'fas-minus-square'
                      : 'far-square'
                  }
                  data={!(selectedItems?.length === dealerDocumentList.count)}
                  onClick={toggleSelectAll}
                  active={selectedItems?.length === dealerDocumentList.count}
                  htmlElementProps={{ title: lang.selectAll }}
                  render={lang.selectAll}
                />
                &nbsp;
              </>
            )}
            <ProductDocumentsDealerFilters />
          </>
        }
        render={
          <>
            <DealerDocumentList />
            {views.add && <ProductDealerDocumentAdd />}
          </>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={dealerDocumentList.count}
            offset={dealerDocumentList.offset}
            limit={dealerDocumentList.limit}
            onLimitChange={(limit) =>
              dealerDocumentList.setDealerDocumentFilters((old) => ({
                ...old,
                limit,
                offset: 0,
              }))
            }
            onOffsetChange={(offset) => dealerDocumentList.setDealerDocumentFilters((old) => ({ ...old, offset }))}
          />
        }
      />
    </>
  );
}
